import { APP_VERSION } from '@globals/appVersion'
import { getEtag } from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { getAppVersion } from './appVersion'
import { isDomainEnabled } from './domainEnabledUtil'
import {
  NavigationBlockerOnReleased,
  useRegisterNavigationBlocker,
} from './NavigationBlockerContext'
import { hasNewAppVersion, hasNewEtag } from './routeReloaderUtils'

const TEN_MINUTE_INTERVAL = 60 * 10 * 1000
const ROUTE_RELOAD_HANDLER_ID = 'route-reload'

const RouteReloader: React.FC = () => {
  // `version.json` is new and depends on how the CDN caches that key, so in
  // production it runs on our own domains only; customer tenants stay on the
  // etag poll until we have confidence there.
  const isAppVersionEnabled = isDomainEnabled()
  const [initialEtag, setInitialEtag] = useState<string>()
  const { data: versionIdentifier } = useQuery({
    queryKey: [isAppVersionEnabled ? 'appVersion' : 'getEtag'],
    queryFn: isAppVersionEnabled ? getAppVersion : getEtag,
    // We will use both stale time and the refetch interval
    // Stale time: Used for the refetch on window focus, so if the user has been away for 10 minutes
    // upon revisiting the tab, data will be refetched
    // Refetch interval: This only runs when you are active on the page, so if you are on the page for 10 minutes
    // the app will refetch in order to update the app.
    staleTime: TEN_MINUTE_INTERVAL,
    refetchInterval: TEN_MINUTE_INTERVAL,
    refetchOnWindowFocus: true, // This will only run if data is stale
  })

  useEffect(() => {
    if (!isAppVersionEnabled) {
      setInitialEtag(initialValue => initialValue ?? versionIdentifier)
    }
  }, [isAppVersionEnabled, versionIdentifier])

  const hasNewVersion = isAppVersionEnabled
    ? hasNewAppVersion(APP_VERSION, versionIdentifier)
    : hasNewEtag(initialEtag, versionIdentifier)

  // Picking up a new version means a full page load, so it waits for the user to
  // leave the route of their own accord rather than interrupting whatever they
  // are reading. Pathname rather than `location.key`: writing search params
  // mints a new key (even with `replace: true`), which would reload while the
  // user is only filtering, paginating, or advancing a query-param driven step.
  const onReleased = useCallback<NavigationBlockerOnReleased>(
    ({ currentLocation, nextLocation }) => {
      if (!hasNewVersion || currentLocation.pathname === nextLocation.pathname) {
        return
      }
      window.location.assign(`${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`)
      return 'handled' as const
    },
    [hasNewVersion],
  )

  useRegisterNavigationBlocker({
    id: ROUTE_RELOAD_HANDLER_ID,
    priority: 0,
    shouldBlock: false,
    onReleased,
  })

  return null
}

export default RouteReloader
